import Header from 'components/headers/wallet'
import NationalCode from 'assets/images/feature_3.png'
import FormikInput from 'components/formikFields/input'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import useGetKyc from './hooks/useGetKyc'
import useAddNationalCode from './hooks/useAddNationalCode'

const validationSchema = Yup.object().shape({
    nationalCode: Yup.string()
        .matches(/^\d{10}$/, 'کد ملی باید دقیقا ۱۰ رقم باشد')
        .max(10, 'کد ملی نمی‌تواند بیشتر از ۱۰ رقم باشد')
        .required('کد ملی الزامی است'),
})

/**
 * A national code that is already on file can never be changed from here.
 *
 * The backend refuses any resubmission the moment `national_code` is set —
 * 409 whether or not `kyc` reached «approved» (`account_management/views/
 * kyc_view.py`: the `ksia` branch and the bare `if national_code` branch both
 * return CONFLICT). The page used to gate on `kyc_status === 'ksia'` alone, so
 * anyone whose code was recorded without that status saw an editable field, a
 * live «تایید» button, and — on tapping it — «مشکلی پیش آمده!». Lock on the
 * same condition the server enforces.
 */
const isLocked = (data?: { national_code?: string | null }) =>
    Boolean(data?.national_code)

/** Locked *and* the KYC actually completed — the only case worth celebrating. */
const isVerified = (data?: { national_code?: string | null; kyc_status?: string }) =>
    isLocked(data) && data?.kyc_status === 'ksia'

export default function AuthNationalCode() {
    const { data, refetch } = useGetKyc()
    const { mutate } = useAddNationalCode(refetch)
    const handleSubmit = async (values: { nationalCode: string }) => {
        mutate(values.nationalCode)
    }
    const locked = isLocked(data)

    const initialValues = {
        nationalCode: data?.national_code || '',
    }

    return (
        <div className="h-screen flex flex-col">
            <div className="grow-0">
                <Header title="احراز هویت با کد ملی" isGreen />
            </div>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                <Form className="flex flex-col grow">
                    <div className="grow flex flex-col">
                        <div className=" w-full  flex justify-center items-center mt-8">
                            <img
                                src={NationalCode}
                                alt="national code"
                                className="w-20 h-20"
                            />
                        </div>
                        <p className=" text-center mt-4 px-4">
                            برای تکمیل فرایند پرداخت و تایید هویت خود، لطفاً کد
                            ملی خود را وارد کنید. این مرحله برای اطمینان از صحت
                            اطلاعات و افزایش امنیت پرداخت ضروری است. بنا به
                            دستور بانک مرکزی، این احراز هویت برای انجام
                            پرداخت‌های آنلاین الزامی است.
                        </p>
                        <div className="px-4 mt-6">
                            <FormikInput
                                name="nationalCode"
                                label="کد ملی"
                                placeholder="کد ملی خود را وارد کنید"
                                required
                                maxLength={10}
                                readOnly={locked}
                                disabled={locked}
                            />
                            {isVerified(data) ? (
                                <p className="text-green-1 mt-4 text-sm">
                                    کد ملی شما با موفقیت تایید شد و فرآیند احراز
                                    هویت با موفقیت انجام شد.
                                </p>
                            ) : locked ? (
                                <p className="text-gray-1 mt-4 text-sm">
                                    کد ملی شما ثبت شده است و قابل تغییر نیست.
                                    اگر نیاز به اصلاح آن دارید، با پشتیبانی
                                    تماس بگیرید.
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <div className="grow-0 px-2 mb-4">
                        <button
                            type="submit"
                            className="w-full bg-green-1 text-white py-2 rounded-lg disabled:opacity-50"
                            disabled={locked}
                        >
                            تایید
                        </button>
                    </div>
                </Form>
            </Formik>
        </div>
    )
}
